import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import RichTextEditor from '../components/RichTextEditor';
import {
  Plus, Pencil, Trash2, X, Newspaper, ArrowRight,
  Upload, Image, Loader2, ChevronDown, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const emptyArticle = {
  title: '',
  content: '',
  content_type: 'text',
  pdf_url: '',
  category: '',
  author: '',
  date: new Date().toISOString().split('T')[0],
  image: '',
};

const ImageUploader = ({ value, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}`);
      toast.success('تم رفع الصورة');
    } catch { toast.error('فشل رفع الصورة'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium mb-1">صورة المقالة</label>
      {value && (
        <div className="relative w-full h-32 rounded-lg overflow-hidden bg-muted mb-2">
          <img src={value} alt="معاينة" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      )}
      <div className="flex gap-2">
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="رابط الصورة أو ارفع صورة..." className="flex-1" data-testid="article-image-input" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="shrink-0" data-testid="upload-image-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        </Button>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleUpload} />
    </div>
  );
};

const CategoryPicker = ({ value, onChange, savedCategories, onDeleteCategory }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium mb-1">التصنيف *</label>
      <div className="flex gap-1">
        <Input value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => setOpen(true)} placeholder="اكتب أو اختر..." data-testid="article-category-input" />
        <Button type="button" variant="outline" size="icon" onClick={() => setOpen(!open)} className="shrink-0"><ChevronDown className="w-4 h-4" /></Button>
      </div>
      {open && savedCategories.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="category-dropdown">
          {savedCategories.map((cat) => (
            <div key={cat} className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 cursor-pointer group">
              <span className="flex-1" onClick={() => { onChange(cat); setOpen(false); }}>{cat}</span>
              <button className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-1" onClick={(e) => { e.stopPropagation(); onDeleteCategory(cat); }} data-testid={`delete-category-${cat}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PdfUploader = ({ value, onChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('يرجى رفع ملف PDF فقط'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/pdf', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}`);
      toast.success('تم رفع ملف PDF بنجاح');
    } catch { toast.error('فشل رفع الملف'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-3">
      {value ? (
        <div className="border-2 border-green-200 bg-green-50 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-green-600" />
            <div>
              <p className="font-semibold text-green-800">تم رفع ملف PDF</p>
              <p className="text-sm text-green-600 truncate max-w-[300px]">{value.split('/').pop()}</p>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => onChange('')} className="text-red-500 border-red-200 hover:bg-red-50">
            <X className="w-4 h-4 ml-1" /> إزالة
          </Button>
        </div>
      ) : (
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
        >
          {uploading ? (
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary" />
          ) : (
            <>
              <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
              <p className="font-semibold text-gray-600">اضغط لرفع ملف PDF</p>
              <p className="text-sm text-gray-400 mt-1">الحد الأقصى: 20 ميجابايت</p>
            </>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={handleUpload} />
    </div>
  );
};

const AdminArticlesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);
  const [savedCategories, setSavedCategories] = useState([]);

  useEffect(() => {
    if (!user?.is_admin) { navigate('/login'); return; }
    loadArticles();
    loadCategories();
  }, []);

  const loadArticles = async () => {
    try { const res = await api.get('/articles'); setArticles(res.data); }
    catch { toast.error('فشل تحميل المقالات'); }
    finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try { const res = await api.get('/admin/article-categories'); setSavedCategories(res.data); } catch {}
  };

  const openCreate = () => {
    setEditingArticle(null);
    setForm(emptyArticle);
    setModalOpen(true);
  };

  const openEdit = (article) => {
    setEditingArticle(article);
    setForm({
      title: article.title,
      content: article.content || '',
      content_type: article.pdf_url ? 'pdf' : 'text',
      pdf_url: article.pdf_url || '',
      category: article.category,
      author: article.author,
      date: article.date,
      image: article.image,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.category || !form.author) {
      toast.error('يرجى ملء الحقول المطلوبة (العنوان، التصنيف، الكاتب)');
      return;
    }
    if (form.content_type === 'pdf' && !form.pdf_url) {
      toast.error('يرجى رفع ملف PDF');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        content: form.content_type === 'text' ? form.content : '',
        pdf_url: form.content_type === 'pdf' ? form.pdf_url : '',
        category: form.category,
        author: form.author,
        date: form.date,
        image: form.image,
      };
      if (editingArticle) {
        await api.put(`/admin/articles/${editingArticle.id}`, payload);
        toast.success('تم تحديث المقالة');
      } else {
        await api.post('/admin/articles', payload);
        toast.success('تم إضافة المقالة');
      }
      setModalOpen(false);
      loadArticles();
      loadCategories();
    } catch { toast.error('فشل حفظ المقالة'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (article) => {
    if (!window.confirm(`هل تريد حذف المقالة "${article.title}"؟`)) return;
    try { await api.delete(`/admin/articles/${article.id}`); toast.success('تم حذف المقالة'); loadArticles(); }
    catch { toast.error('فشل حذف المقالة'); }
  };

  const handleDeleteCategory = async (cat) => {
    try { await api.delete(`/admin/article-categories/${encodeURIComponent(cat)}`); setSavedCategories((prev) => prev.filter((c) => c !== cat)); toast.success('تم حذف التصنيف'); }
    catch { toast.error('فشل حذف التصنيف'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div></div>;

  return (
    <div className="space-y-8" data-testid="admin-articles-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin')} data-testid="back-to-admin"><ArrowRight className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-4xl font-bold text-accent">إدارة المقالات</h1>
            <p className="text-muted-foreground text-lg mt-1">{articles.length} مقالة</p>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-primary hover:bg-primary/90" data-testid="create-article-btn">
          <Plus className="w-5 h-5 ml-2" /> إضافة مقالة
        </Button>
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-16">
          <Newspaper className="w-16 h-16 text-muted mx-auto mb-4 opacity-50" />
          <p className="text-lg text-muted-foreground">لا توجد مقالات بعد</p>
        </div>
      ) : (
        <div className="space-y-4">
          {articles.map((article) => (
            <Card key={article.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`article-row-${article.id}`}>
              <div className="flex items-center gap-4">
                {article.image ? (
                  <img src={article.image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    {article.pdf_url ? <FileText className="w-6 h-6 text-red-500" /> : <Image className="w-6 h-6 text-muted-foreground" />}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-accent truncate">{article.title}</h3>
                    {article.pdf_url && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium shrink-0">PDF</span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                    <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs">{article.category}</span>
                    <span>{article.author}</span>
                    <span>{article.date}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openEdit(article)} data-testid={`edit-article-${article.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(article)} data-testid={`delete-article-${article.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="article-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-accent">{editingArticle ? 'تعديل المقالة' : 'إضافة مقالة جديدة'}</h2>
              <button onClick={() => setModalOpen(false)}><X className="w-6 h-6 text-muted-foreground" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">العنوان *</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="article-title-input" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <CategoryPicker value={form.category} onChange={(val) => setForm({ ...form, category: val })} savedCategories={savedCategories} onDeleteCategory={handleDeleteCategory} />
                <div>
                  <label className="block text-sm font-medium mb-1">الكاتب *</label>
                  <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="article-author-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">التاريخ</label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="article-date-input" />
              </div>

              <ImageUploader value={form.image} onChange={(url) => setForm({ ...form, image: url })} />

              {/* Content Type Toggle */}
              <div>
                <label className="block text-sm font-medium mb-2">نوع المحتوى</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, content_type: 'text' })}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold transition-all ${
                      form.content_type === 'text' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                    data-testid="content-type-text"
                  >
                    <Pencil className="w-5 h-5" /> كتابة نص
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, content_type: 'pdf' })}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold transition-all ${
                      form.content_type === 'pdf' ? 'border-red-400 bg-red-50 text-red-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                    data-testid="content-type-pdf"
                  >
                    <FileText className="w-5 h-5" /> رفع PDF
                  </button>
                </div>
              </div>

              {/* Text Editor or PDF Upload */}
              {form.content_type === 'text' ? (
                <div>
                  <label className="block text-sm font-medium mb-1">المحتوى</label>
                  <RichTextEditor value={form.content} onChange={(val) => setForm({ ...form, content: val })} />
                </div>
              ) : (
                <PdfUploader value={form.pdf_url} onChange={(url) => setForm({ ...form, pdf_url: url })} />
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-article-btn">
                {saving ? 'جارٍ الحفظ...' : editingArticle ? 'تحديث' : 'إضافة'}
              </Button>
              <Button variant="outline" onClick={() => setModalOpen(false)} className="flex-1">إلغاء</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArticlesPage;
